import type { Theme } from '@mui/material';
import { Box } from '@mui/material';
import React from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { CarouselItemContent } from '../../template/imageCarouselTypes';
import ImageCarouselSlideControls from './ImageCarouselSlideControls';
import ImageCarouselSlideFooter from './ImageCarouselSlideFooter';
import ImageCarouselSlideMediaZone from './ImageCarouselSlideMediaZone';
import VideoMetadataPromptDialog from './VideoMetadataPromptDialog';
import { useImageCarouselSlideCardState } from './useImageCarouselSlideCardState';

function slideCardBoxShadow(over: boolean, draggingVideoId: string | null) {
  return (theme: Theme) => {
    if (over) return `0 0 0 3px ${theme.palette.primary.main}`;
    if (draggingVideoId) return `0 0 0 2px ${theme.palette.primary.light}`;
    return 'none';
  };
}

function slideCardSx(over: boolean, draggingVideoId: string | null) {
  return {
    flex: '1 1 260px',
    minWidth: 240,
    maxWidth: 360,
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    bgcolor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '&:hover .slide-controls': { opacity: 1 },
    boxShadow: slideCardBoxShadow(over, draggingVideoId),
    transition: 'box-shadow 0.12s ease',
  } as const;
}

interface ImageCarouselSlideCardProps {
  index: number;
  item: CarouselItemContent;
  uploading: boolean;
  onFile: (file: File) => void;
  onRemove: () => void;
  onUpdateHeading: (value: string) => void;
  onUpdateText: (value: string) => void;
  fundOpportunityId: string;
  backendService: BackendService;
  onToggleVideo: (isVideo: boolean) => void;
  onVideoURLChange: (url: string) => void;
  onLibraryVideoAttached: (externalId: string) => void;
}

/**
 * One carousel slide: media zone, hover controls, image/video toggle, and content fields.
 * A library video dragged from the sidebar's Media tab, or a video file, can be dropped
 * anywhere on it to replace its media.
 */
export default function ImageCarouselSlideCard({
  index,
  item,
  uploading,
  onFile,
  onRemove,
  onUpdateHeading,
  onUpdateText,
  fundOpportunityId,
  backendService,
  onToggleVideo,
  onVideoURLChange,
  onLibraryVideoAttached,
}: ImageCarouselSlideCardProps) {
  const {
    draggingVideoId,
    videoDrop,
    upload,
    handleVideoFile,
    handleCardDrop,
    handleCardDragOver,
    openLibrary,
    getVideoPlaybackUrl,
  } = useImageCarouselSlideCardState({
    index,
    item,
    fundOpportunityId,
    backendService,
    onToggleVideo,
    onLibraryVideoAttached,
  });

  return (
    <Box
      sx={slideCardSx(videoDrop.over, draggingVideoId)}
      onDragOver={handleCardDragOver}
      onDragLeave={videoDrop.onDragLeave}
      onDrop={handleCardDrop}
    >
      <ImageCarouselSlideMediaZone
        index={index}
        item={item}
        uploading={uploading}
        upload={upload}
        getVideoPlaybackUrl={getVideoPlaybackUrl}
        onFile={onFile}
        onVideoFile={handleVideoFile}
        onVideoUrl={onVideoURLChange}
        onOpenLibrary={openLibrary}
      />
      <ImageCarouselSlideControls
        index={index}
        onFile={onFile}
        onVideoUrl={onVideoURLChange}
        onRemove={onRemove}
      />
      <ImageCarouselSlideFooter
        index={index}
        item={item}
        onToggleVideo={onToggleVideo}
        onVideoURLChange={onVideoURLChange}
        onOpenLibrary={openLibrary}
        onUpdateHeading={onUpdateHeading}
        onUpdateText={onUpdateText}
      />
      <VideoMetadataPromptDialog
        open={upload.phase === 'awaiting-metadata'}
        fileName={upload.stashedFile?.name}
        onCancel={upload.cancelMetadataPrompt}
        onConfirm={(title, description) => void upload.confirmMetadata(title, description)}
      />
    </Box>
  );
}
